/* eslint-disable react/require-default-props */
import { VbzButton } from "@smiles/voebiz-ui-kit";
import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { v4 } from "uuid";

import { AirlineLogo } from "../../assets/index";
import { getDate, getHours } from "../../utils/index";
import { IArrival, IDeparture, IPassenger } from "../../@types/flight-order";
import { LINK_CHECKIN_FLIGHT, TYPE_COMPONENT } from "../../constants";

export interface FlightCardProps {
  type: string;
  locator: string;
  changed?: boolean;
  travelers: IPassenger[];
  arrival: IArrival;
  departure: IDeparture;
  stops: string;
  duration: {
    hours: string;
    minutes: string;
  };
  onClickDetail?: () => void;
}

const FlightCard: FC<FlightCardProps> = ({
  type,
  locator,
  changed = false,
  travelers,
  arrival,
  departure,
  stops,
  duration,
  onClickDetail,
}) => {
  const { t } = useTranslation("flightCard");

  const renderTravelers = () => {
    if (travelers.length === 1) {
      return (
        <div className="flight-card__traveler">
          <div className="icon-title-mobile">
            <div className="flight-card__icon flight-card__icon--medium">
              <span className="material-icons" role="img" aria-label="Viajante" title="Viajante">
                &#xe7fd;
              </span>
            </div>
            <div className="flight-card__title">{t("traveler")}</div>
          </div>
          <div className="flight-card__traveler-info">
            <span>{`${travelers[0].firstName} ${travelers[0].lastName}`}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="flight-card__traveler flight-card__traveler--multiple">
        <div className="flight-card__icon flight-card__icon--medium">
          <span className="material-icons" role="img" aria-label="Viajantes" title="Viajantes">
            &#xe7fe;
          </span>
        </div>

        <div className="flight-card__title">{t("traveler")}</div>

        <div className="flight-card__traveler-info">
          <span>{`${travelers[0].firstName} ${travelers[0].lastName}`}</span>
          <span className="flight-card__traveler-number">+{travelers.length - 1}</span>
        </div>

        <ul className="flight-card__traveler-list">
          <div className="flight-card__icon flight-card__icon--medium">
            <span className="material-icons" role="img" aria-label="Viajantes" title="Viajantes">
              &#xe7fe;
            </span>
          </div>
          <div className="flight-card__title">{t("traveler")}</div>
          <hr className="flight-card__traveler-line" />
          <div className="flight-card__traveler-items">
            {travelers.map((traveler) => (
              <li className="flight-card__traveler-item" key={v4()}>
                {`${traveler.firstName} ${traveler.lastName}`}
              </li>
            ))}
          </div>
        </ul>
      </div>
    );
  };

  return (
    <div className="flight-card">
      {changed && <span className="flight-card__tag">{t("changedFlight")}</span>}

      {/* Localizador */}
      <div className="flight-card__locator">
        <div className="flight-card__locator--desktop">
          <div className="flight-card__title flight-card__locator-title">{t("locator")}</div>
          <div className="flight-card__locator-info">
            <div className="flight-card__icon flight-card__icon--medium flight-card__icon--rotate flight-card__locator-icon mb-0">
              <span className="material-icons">&#xe195;</span>
            </div>
            <div className="flight-card__locator-text">{locator}</div>
          </div>
        </div>

        <div className="flight-card__locator--mobile">
          <div className="flight-card__locator-cia">
            <img className="airLaneLogo" src={AirlineLogo} alt={t("alt")} />
          </div>
          <div className="flight-card__title flight-card__locator-title">
            {t("locator")}: {locator}
          </div>
        </div>
      </div>

      {/* Origem e Destino */}
      <div className="flight-card__originDestiny">
        <div className="icon-title-mobile">
          <div className="flight-card__icon flight-card__icon--medium desktop-none">
            <span className="material-icons">&#xe935;</span>
          </div>
          <div className="flight-card__title flight-card__originDestiny-title">{t("originAndDestiny")}</div>
        </div>

        <div className="flight-card__originDestiny-info">
          <div className="flight-card__originDestiny-origin" data-testid="origin">
            <span>{getHours(departure.date, "h")}</span> <span>{departure.airport.code}</span>
          </div>

          <div className="flight-card__icon flight-card__icon--small flight-card__icon--rotate flight-card__originDestiny-icon">
            <span className="material-icons">&#xe195;</span>
          </div>

          <div className="flight-card__originDestiny-destiny" data-testid="destiny">
            <span>{arrival.airport.code}</span> <span>{getHours(arrival.date, "h")}</span>
          </div>
        </div>

        <span className="flight-card__originDestiny-length mobile-none">
          {stops} {t("stop")} - {`${duration.hours}h${duration.minutes}min`} {t("flyingTime")}
        </span>
      </div>

      {/* Data/Hora */}
      <div className="flight-card__datetime">
        <div className="icon-title-mobile">
          <div className="flight-card__icon flight-card__icon--medium">
            <span className="material-icons">&#xe935;</span>
          </div>
          <div className="flight-card__title">{t("dateTime")}</div>
        </div>

        <div className="flight-card__datetime-info">
          {getDate(departure.date)} - {getHours(departure.date, "h")}
        </div>
      </div>

      {/* Viajante */}
      {renderTravelers()}

      <div className="flight-card__btn">
        {type === TYPE_COMPONENT ? (
          <VbzButton
            data-testid="btn-checkin"
            type="button"
            text={t("btn_checkin")}
            color="primary"
            onClick={() => {
              window.location.href = LINK_CHECKIN_FLIGHT;
            }}
          />
        ) : (
          <>
            <VbzButton type="button" color="hyperlink" text={t("hyperlink_showMore")} onClick={onClickDetail} />
            <div onClick={onClickDetail} onKeyPress={onClickDetail} role="menuitem" tabIndex={0} className="arrowBtn">
              <span className="material-icons" style={{ position: "relative", top: "4px", marginLeft: "22px" }}>
                keyboard_arrow_down
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FlightCard;
